import { useEffect, useCallback, useState } from 'react';
import { cn } from '../../utils';
import { useSwipeDrawer } from '../../hooks/use-swipe-drawer';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  width?: number;
  className?: string;
  swipeable?: boolean;
  'aria-label'?: string;
}

const DEFAULT_WIDTH = 240; // matches sidebar w-60

export function Drawer({
  open,
  onOpenChange,
  children,
  width = DEFAULT_WIDTH,
  className,
  swipeable = true,
  'aria-label': ariaLabel,
}: DrawerProps) {
  // Track whether the overlay is "visible" (open or animating closed)
  const [visible, setVisible] = useState(false);

  const stableOnOpenChange = useCallback(
    (v: boolean) => onOpenChange(v),
    [onOpenChange],
  );

  const { drawerRef, backdropRef, edgeRef, didSwipeRef } = useSwipeDrawer({
    open,
    onOpenChange: stableOnOpenChange,
    width,
    swipeable,
  });

  // When open becomes true, make overlay visible
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisible(true);
    }
  }, [open]);

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  // Apply open/close transforms when state changes (button or backdrop click)
  // Skipped when the swipe hook already handled the animation.
  useEffect(() => {
    if (didSwipeRef.current) {
      // A swipe-to-open updates `open` first and `visible` in the following
      // render. Keep skipping until both states agree, otherwise that second
      // render rewinds the panel off-screen and replays the opening animation.
      if (open === visible) didSwipeRef.current = false;
      return;
    }

    const panel = drawerRef.current;
    const backdrop = backdropRef.current;
    if (!panel || !backdrop) return;

    if (open) {
      // Animate in on next frame
      panel.style.transition = 'none';
      panel.style.transform = `translateX(-${width}px)`;
      backdrop.style.transition = 'none';
      backdrop.style.opacity = '0';

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.transition = 'transform 250ms ease-out';
          panel.style.transform = 'translateX(0px)';
          backdrop.style.transition = 'opacity 250ms ease-out';
          backdrop.style.opacity = '1';
        });
      });
    } else if (visible) {
      // Closing animation
      panel.style.transition = 'transform 250ms ease-out';
      panel.style.transform = `translateX(-${width}px)`;
      backdrop.style.transition = 'opacity 250ms ease-out';
      backdrop.style.opacity = '0';
    }
  }, [open, visible, width, drawerRef, backdropRef, didSwipeRef]);

  // Unmount overlay after close animation finishes
  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      // Only react to the panel's own transform transition
      if (e.target !== drawerRef.current || e.propertyName !== 'transform')
        return;
      if (!open) {
        setVisible(false);
      }
    },
    [open, drawerRef],
  );

  return (
    <>
      {/* Edge zone for swipe-to-open (invisible touch target at left edge) */}
      {swipeable && (
        <div
          ref={edgeRef}
          className="fixed inset-y-0 left-0 z-50 w-5 md:hidden"
          style={{
            touchAction: 'pan-y',
            // Hide edge zone when overlay is interactive
            pointerEvents: visible ? 'none' : 'auto',
          }}
          aria-hidden="true"
        />
      )}

      {/* Overlay — always mounted when swipeable so refs are attached for edge swipe */}
      {(swipeable || visible) && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          role={visible ? 'dialog' : undefined}
          aria-modal={visible ? 'true' : undefined}
          aria-label={visible ? ariaLabel : undefined}
          aria-hidden={!visible}
          style={{ pointerEvents: visible ? 'auto' : 'none' }}
        >
          {/* Backdrop */}
          <div
            ref={backdropRef}
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            style={{ opacity: 0 }}
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            ref={drawerRef}
            className={cn('relative z-10 h-full bg-card', className)}
            style={{
              width,
              transform: `translateX(-${width}px)`,
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {children}
          </div>
        </div>
      )}
    </>
  );
}
